"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useModel = useModel;
exports.useModelInternalState = useModelInternalState;
exports.useMutator = useMutator;
var _react = require("react");
var _modelStore = require("./modelStore");
/*
Two things can cause the return value from useModel to change:
1. The model store data changes
2. The endpoint (signature) changes

If the signature changes, and the data is present, we want to return the new data immediately (synchronously), in the
same render cycle. We can't wait for the state to be updated in the next render cycle. But the state still needs to
update so that child components will re-render.

What we can do is always slice the data and return that. But if the model store data changes, then we trigger an update
to our local state, which will cause a re-render. This will cause the data to be sliced again, but this time it will be
the new data.
*/

/**
 * Provides automatically updating data models.
 * @param endpoint An object contained in the `api` namespace. If falsey, useModel will return null.
 * @returns An array of data, or null if the endpoint was falsey.
 */
function useModel(endpoint) {
  // console.log('useModel', endpoint)
  // Use the context from the ModelStoreContext
  const modelStore = (0, _react.useContext)(_modelStore.ModelStoreContext);
  if (!modelStore) {
    throw new Error('There must be an enclosing ModelStoreProvider to use useModel.');
  }
  const {
    getState,
    requestData,
    subscribe
  } = modelStore;

  /** Used to trigger reslicing when the model store data changes. */
  const [updateCounter, setUpdateCounter] = (0, _react.useState)(0);

  /** A string signature which uniquely identifies the endpoint and its parameters. */
  let signature = endpoint ? endpoint.apiPath + JSON.stringify(endpoint.params) : '';

  // Subscribe to the modelStore. If the endpoint signature changes, resubscribe.
  (0, _react.useEffect)(() => {
    if (!endpoint || !endpoint.model) {
      return () => {};
    }
    return subscribe(endpoint.model, () => {
      setUpdateCounter(c => c + 1);
    });
  }, [signature]);

  /**
   * The sliced data from the model store. It is filtered according to the endpoint and its parameters. The dependency
   * array has two items: the signature, so that we will re-slice data if the endpoint signature changes, and the
   * updateCounter, so that we will re-slice data if the model store data changes.
   */
  const returnValue = (0, _react.useMemo)(() => {
    // Users may provide a falsey endpoint to conditionally request data without breaking the rules of hooks.
    if (!endpoint) return null;
    let state = getState();
    if (endpoint.tryFilter) {
      // Try the filter function first, and return if filtered has any data in it at all.
      let filtered = getModelSlice(state.model, endpoint);
      if (filtered && filtered.length > 0) {
        return filtered;
      }
    }

    // Request the data if it has not already been requested.
    if (!state.signatureStatus[signature]) {
      requestData(endpoint, signature);

      // If the endpoint uses `key`, then it's possible that the requested data is already present. If this is the
      // case, requestData will set the signature status to 'complete'. So get the state again.
      state = getState();
    }
    if (state.signatureStatus[signature] === 'complete') {
      return getModelSlice(state.model, endpoint);
    } else {
      return null;
    }
  }, [signature, updateCounter]);
  return returnValue;
}

/**
 * Get the appropriate data from the model according to the parameters of the endpoint. If endpoint is falsey, returns null.
 * @param model The model store data.
 * @param endpoint The endpoint specifying the data to return.
 */
function getModelSlice(model, endpoint) {
  if (!endpoint) {
    return null;
  }

  // Perform some endpoint validation
  if (!endpoint.model) {
    throw new TypeError(`Endpoint ${endpoint.apiPath} does not contain a 'model' property, so it cannot be used with useModel(). You can still call execute() on the endpoint directly.`);
  }
  if (!endpoint.execute) {
    throw new TypeError('You must provide an object from the `api` namespace.');
  }

  // Use the filter function to return the appropriate data, otherwise return all the data
  if (endpoint.filter) {
    return endpoint.filter(model[endpoint.model]);
  } else {
    return model[endpoint.model];
  }
}

/**
 * Provides automatically updating data models.
 * @param endpoint A factory function contained in the `api` namespace.
 * @returns A function which, when called with params, performs a mutation of data on the server and updates the model store.
 */
function useMutator(endpoint) {
  // Use the context from the modelStore
  const modelStore = (0, _react.useContext)(_modelStore.ModelStoreContext);
  if (!modelStore) {
    throw new Error('There must be an enclosing ModelStoreProvider to use useMutator.');
  }
  const {
    requestMutation
  } = modelStore;
  if (typeof endpoint !== 'function') {
    throw new TypeError('You must provide a factory function from the `api` namespace.');
  }
  return async params => {
    return requestMutation(endpoint(params));
  };
}

/**
 * Returns the internal model state, useful to see statistics about modelly things.
 */
function useModelInternalState() {
  const modelStore = (0, _react.useContext)(_modelStore.ModelStoreContext);
  if (!modelStore) {
    throw new Error('There must be an enclosing ModelStoreProvider to use useModelInternalState.');
  }
  const {
    getState,
    internalStateSubscribe
  } = modelStore;
  const [internalState, setInternalState] = (0, _react.useState)(getState());
  (0, _react.useEffect)(() => {
    return internalStateSubscribe(() => {
      // console.log('useModelInternalState: internal state was updated', getState())

      // We use setTimeout here so that clients do not get the "Cannot update a component (`App`) while rendering a
      // different component (`Orders`)" error. If there's a better way, I'm all ears.
      setTimeout(() => {
        setInternalState(getState());
      }, 0);
    });
  });
  return internalState;
}